import { useCallback, useRef, useState } from 'react'
import { adminOrderService, shopService } from '../services'
import { getOrderPayableAmount } from '../utils/paymentStatusDisplay'
import {
  ledgerBalanceAmount,
  ledgerBalanceCoversOrder,
} from '../utils/creditExposureBalance'
import { orderHasExistingBalanceApplied } from '../utils/orderExistingBalance'

/**
 * When the shop has usable SAP balance, confirm before opening admin payment-proof upload.
 */
export function useAdminPaymentProofUploadGate({ order, onBalanceApplied }) {
  const [visible, setVisible] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState(null)
  const pendingOpenRef = useRef(null)

  const orgId = order?.org_id
  const orderId = order?.order_id
  const payable = order ? getOrderPayableAmount(order) : 0
  const alreadyApplied = order ? orderHasExistingBalanceApplied(order) : false
  const canPrompt =
    Boolean(orgId && orderId && payable > 0 && !alreadyApplied)

  const close = useCallback(() => {
    setVisible(false)
    setError(null)
    pendingOpenRef.current = null
  }, [])

  const fetchExposure = useCallback(async () => {
    const balance = await shopService.getBalance(orgId)
    return ledgerBalanceAmount(balance)
  }, [orgId])

  const requestOpenPaymentProof = useCallback(
    async (openFn) => {
      if (!canPrompt) {
        openFn()
        return
      }
      let exposure = null
      try {
        exposure = await fetchExposure()
      } catch {
        openFn()
        return
      }
      if (!ledgerBalanceCoversOrder(exposure, payable)) {
        openFn()
        return
      }
      pendingOpenRef.current = openFn
      setError(null)
      setVisible(true)
    },
    [canPrompt, fetchExposure, payable],
  )

  const confirmUploadProofDeposit = useCallback(() => {
    const openFn = pendingOpenRef.current
    close()
    openFn?.()
  }, [close])

  const proceedWithAvailableBalance = useCallback(async () => {
    if (!orderId) return
    setApplying(true)
    setError(null)
    try {
      let exposure = null
      try {
        exposure = await fetchExposure()
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            'Unable to load balance',
        )
        return
      }
      if (!ledgerBalanceCoversOrder(exposure, payable)) {
        setError(
          'Ledger balance must be greater than the order amount to use existing balance.',
        )
        return
      }
      await adminOrderService.applyExistingBalance(orderId)
      close()
      await onBalanceApplied?.()
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to submit existing balance',
      )
    } finally {
      setApplying(false)
    }
  }, [close, fetchExposure, onBalanceApplied, orderId, payable])

  return {
    balanceChoiceVisible: visible,
    balanceChoiceApplying: applying,
    balanceChoiceError: error,
    requestOpenPaymentProof,
    closeBalanceChoice: close,
    confirmUploadProofDeposit,
    proceedWithAvailableBalance,
  }
}
